import { can, formatCode, stockLevel } from '@pl/domain';
import type { SiteDto, SkuDto, StockBoxDto, StockRowDto } from '@pl/domain';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { shortDateTime, stateWords } from '../../lib/format';
import { useMe, useSites, useSkus, useStock, useStockBoxes } from '../../lib/queries';
import { Chip, Empty, ErrorNote, RefetchNote, Spinner, SkuTag } from '../../ui/bits';
import { Icon } from '../../ui/Icon';
import { CsvDownloads } from '../CsvDownloads';
import { useSiteScope } from '../useSiteScope';
import s from './Stock.module.css';

/** A row's flags, worst first: `stockLevel`'s levels that need someone to look, and where. */
type Flag = { level: 'short' | 'out' | 'low'; sites: SiteDto[] };
const FLAG_ORDER = ['short', 'out', 'low'] as const;
const FLAG_WORD: Record<Flag['level'], string> = { short: 'Short', out: 'Out', low: 'Low' };

interface SkuRow {
  sku: SkuDto;
  type: string;
  jacket?: string;
  perSite: Map<string, StockRowDto>;
  totalAvailable: number;
  totalInStock: number;
  /** Short, out or low at one or more live sites, judged site by site (`stockLevel`), never on the total. */
  flags: Flag[];
}

interface SkuGroup {
  type: string;
  jacket?: string;
  rows: SkuRow[];
}

function typeOf(sku: SkuDto): string {
  const t = sku.attributes?.type;
  if (typeof t === 'string' && t.trim()) return t;
  return sku.name.split(/\s+/)[0] ?? sku.name;
}

function jacketOf(sku: SkuDto): string | undefined {
  const j = sku.attributes?.jacket;
  return typeof j === 'string' && j.trim() ? j : undefined;
}

function csvCell(v: string | number): string {
  const str = String(v);
  return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
}

function StockExpansion({ site, sku, totalCols }: { site: SiteDto; sku: SkuDto; totalCols: number }) {
  const boxesQuery = useStockBoxes(site.id, sku.id);
  const boxes: StockBoxDto[] = boxesQuery.data ?? [];
  return (
    <tr className={s.x}>
      <td colSpan={totalCols}>
        <div className={s.xb}>
          <div className={s.xh}>
            <span>
              Showing boxes at <b>{site.name}</b>. In stock excludes boxes at the dock.
            </span>
          </div>
          <RefetchNote queries={[boxesQuery]} />
          {boxesQuery.isPending ? (
            <Spinner label="Loading boxes" />
          ) : boxesQuery.isLoadingError ? (
            <ErrorNote error={boxesQuery.error} />
          ) : boxes.length === 0 ? (
            <Empty title="No boxes here" />
          ) : (
            <table className={s.bx}>
              <thead>
                <tr>
                  <th>Box</th>
                  <th>Pallet</th>
                  <th>Received</th>
                  <th>Status</th>
                  <th>Order</th>
                </tr>
              </thead>
              <tbody>
                {boxes.map((b) => (
                  <tr key={b.id}>
                    <td className="mono">
                      <Link to={`/office/boxes/${b.code}`}>{formatCode(b.code)}</Link>
                    </td>
                    <td className="mono">{b.palletCode ? formatCode(b.palletCode) : '—'}</td>
                    <td>{b.receivedAt ? shortDateTime(b.receivedAt, site.timezone) : '—'}</td>
                    <td>
                      <Chip tone={b.state === 'in_stock' ? 'ok' : b.state === 'picked' ? 'accent' : undefined} dot={b.state === 'in_stock'}>
                        {stateWords(b.state)}
                      </Chip>
                    </td>
                    <td className="mono">{b.orderRef ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </td>
    </tr>
  );
}

/**
 * Download CSV, for anyone who may export (office and admin): the grid as it is shown, then the four
 * whole tables. Closes on Escape, on a click outside, and once something is picked.
 */
function DownloadCsv({ onThisView }: { onThisView: () => void }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const id = useId();
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      button.current?.focus();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return (
    <div className={s.dl} ref={box}>
      <button ref={button} type="button" className="btn" aria-expanded={open} aria-controls={id} onClick={() => setOpen((o) => !o)}>
        <Icon name="download" />
        Download CSV
        <Icon name="chev-down" />
      </button>
      {open && (
        <div id={id} className={s.dlPop}>
          <button
            type="button"
            className={s.dlView}
            onClick={() => {
              onThisView();
              setOpen(false);
            }}
          >
            <span>
              <Icon name="stock" />
              This view, as shown
            </span>
            <small>The grid above, with its site, search and filters.</small>
          </button>
          <div className={s.dlHead}>Whole tables</div>
          <CsvDownloads compact onPick={() => setOpen(false)} />
        </div>
      )}
    </div>
  );
}

export function Stock() {
  const me = useMe().data;
  const sitesQuery = useSites();
  const skusQuery = useSkus();
  const stockQuery = useStock();
  const { siteId, setSiteId } = useSiteScope();
  const [search, setSearch] = useState('');
  const [lowOnly, setLowOnly] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const sites = sitesQuery.data ?? [];
  const skus = skusQuery.data ?? [];
  const stock = stockQuery.data ?? [];
  const liveSites = useMemo(() => sites.filter((x) => x.live), [sites]);
  const displaySites = useMemo(() => (siteId ? sites.filter((x) => x.id === siteId) : sites), [sites, siteId]);
  const expandSite = useMemo(() => (siteId ? sites.find((x) => x.id === siteId) : liveSites[0]), [sites, siteId, liveSites]);

  const rows: SkuRow[] = useMemo(() => {
    return skus.map((sku) => {
      const perSite = new Map<string, StockRowDto>();
      for (const row of stock) if (row.skuId === sku.id) perSite.set(row.siteId, row);
      let totalAvailable = 0;
      let totalInStock = 0;
      const at = new Map<Flag['level'], SiteDto[]>();
      for (const site of liveSites) {
        const row = perSite.get(site.id);
        const available = row?.available ?? 0;
        totalAvailable += available;
        totalInStock += row?.inStock ?? 0;
        const level = stockLevel(available);
        if (level !== 'ok') at.set(level, [...(at.get(level) ?? []), site]);
      }
      const flags = FLAG_ORDER.filter((level) => at.has(level)).map((level) => ({ level, sites: at.get(level)! }));
      return { sku, type: typeOf(sku), jacket: jacketOf(sku), perSite, totalAvailable, totalInStock, flags };
    });
  }, [skus, stock, liveSites]);

  const lowCount = useMemo(() => rows.filter((r) => r.flags.length > 0).length, [rows]);

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (lowOnly && r.flags.length === 0) return false;
      if (!q) return true;
      return r.sku.code.toLowerCase().includes(q) || r.sku.name.toLowerCase().includes(q) || (r.sku.colourName ?? '').toLowerCase().includes(q);
    });
  }, [rows, search, lowOnly]);

  const groups: SkuGroup[] = useMemo(() => {
    const map = new Map<string, SkuGroup>();
    for (const row of visibleRows) {
      const existing = map.get(row.type);
      if (existing) existing.rows.push(row);
      else map.set(row.type, { type: row.type, jacket: row.jacket, rows: [row] });
    }
    for (const group of map.values()) {
      // Only show a jacket subtitle when every SKU in the group shares it.
      if (!group.rows.every((r) => r.jacket === group.jacket)) group.jacket = undefined;
    }
    return [...map.values()].sort((a, b) => a.type.localeCompare(b.type));
  }, [visibleRows]);

  const totalCols = 2 + displaySites.reduce((n, site) => n + (site.live ? 3 : 1), 0) + 2;

  function toggleRow(skuId: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(skuId)) next.delete(skuId);
      else next.add(skuId);
      return next;
    });
  }

  function exportCsv() {
    const header = ['Code', 'Name', 'Color', ...displaySites.flatMap((site) => [`${site.shortCode} in stock`, `${site.shortCode} reserved`, `${site.shortCode} available`])];
    const lines = visibleRows.map((row) => [
      row.sku.code,
      row.sku.name,
      row.sku.colourName ?? '',
      ...displaySites.flatMap((site) => {
        const m = row.perSite.get(site.id);
        return [m?.inStock ?? 0, m?.reserved ?? 0, m?.available ?? 0];
      }),
    ]);
    const csv = [header, ...lines].map((r) => r.map(csvCell).join(',')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'stock.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Stock refreshes every 20 seconds. One refresh that fails keeps the grid it already has and says
  // so above it; only a query with nothing to show takes the page.
  const queries = [sitesQuery, skusQuery, stockQuery];
  const failedQueries = queries.filter((q) => q.isLoadingError);
  if (failedQueries.length > 0) {
    return (
      <div className="panel">
        <div className="panel-b stack" style={{ gap: 14, alignItems: 'flex-start' }}>
          <ErrorNote error={failedQueries[0]!.error} />
          <button type="button" className="btn" onClick={() => failedQueries.forEach((q) => q.refetch())}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!sitesQuery.data || !skusQuery.data || !stockQuery.data) {
    return (
      <div className="row">
        <Spinner label="Loading stock" />
      </div>
    );
  }

  return (
    <div className={s.page}>
      <RefetchNote queries={queries} />
      <div className="fbar">
        <div className="seg">
          <button type="button" className={siteId === undefined ? 'on' : undefined} aria-pressed={siteId === undefined} onClick={() => setSiteId(undefined)}>
            All sites
          </button>
          {sites.map((site) => (
            <button key={site.id} type="button" className={siteId === site.id ? 'on' : undefined} aria-pressed={siteId === site.id} onClick={() => setSiteId(site.id)}>
              <span className="code">{site.shortCode}</span>
              {site.name}
            </button>
          ))}
        </div>
        <button type="button" className={`check${lowOnly ? ' on' : ''}`} aria-pressed={lowOnly} onClick={() => setLowOnly((v) => !v)}>
          <i />
          Low or out <span className="faint mono" style={{ fontSize: 11 }}>{lowCount}</span>
        </button>
        <label className="search">
          <Icon name="search" />
          <input
            type="text"
            placeholder="Search SKUs"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ border: 0, outline: 'none', font: 'inherit', color: 'inherit', background: 'transparent', flex: 1, minWidth: 0 }}
          />
        </label>
        <span className="grow" />
        {me && can(me.role, 'export') ? (
          <DownloadCsv onThisView={exportCsv} />
        ) : (
          <button type="button" className="btn" onClick={exportCsv}>
            <Icon name="download" />
            Export CSV
          </button>
        )}
        <span className={s.legend}>
          <span>In stock</span>
          <span>Reserved</span>
          <b>Available</b>
        </span>
      </div>

      <div className={`panel ${s.gWrap}`}>
        <table className={s.g}>
          <colgroup>
            <col style={{ width: 118 }} />
            {/* 250, not 300: at 1366 px the grid's columns must add up to what the page has, or TOTAL is cut off. */}
            <col style={{ width: 250 }} />
            {displaySites.flatMap((site) =>
              site.live
                ? [<col key={`${site.id}-in`} style={{ width: 78 }} />, <col key={`${site.id}-res`} style={{ width: 78 }} />, <col key={`${site.id}-av`} style={{ width: 86 }} />]
                : [<col key={site.id} style={{ width: 74 }} />],
            )}
            <col style={{ width: 104 }} />
            <col style={{ width: 34 }} />
          </colgroup>
          <thead>
            <tr>
              <th className={s.l} rowSpan={2} style={{ verticalAlign: 'bottom' }}>
                Type
              </th>
              <th className={s.l} rowSpan={2} style={{ verticalAlign: 'bottom' }}>
                SKU · Color
              </th>
              {displaySites.map((site) =>
                site.live ? (
                  <th key={site.id} className={`${s.site} ${s.gs}`} colSpan={3}>
                    <span className="site-tag">{site.shortCode}</span>
                    {site.name}
                  </th>
                ) : (
                  <th key={site.id} className={s.gs} rowSpan={2} style={{ verticalAlign: 'bottom', textAlign: 'center' }}>
                    <span className="site-tag">{site.shortCode}</span>
                    <div style={{ marginTop: 4, textTransform: 'none', letterSpacing: 0, fontWeight: 400, color: 'var(--faint)' }}>Not live</div>
                  </th>
                ),
              )}
              <th className={s.gs} rowSpan={2} style={{ verticalAlign: 'bottom' }}>
                Total
                <div style={{ marginTop: 2, textTransform: 'none', letterSpacing: 0, fontWeight: 400, color: 'var(--faint)' }}>avail. · in stock</div>
              </th>
              <th rowSpan={2}></th>
            </tr>
            <tr>
              {displaySites
                .filter((site) => site.live)
                .flatMap((site) => [
                  <th key={`${site.id}-in`} className={s.gs}>In stock</th>,
                  <th key={`${site.id}-res`}>Reserved</th>,
                  <th key={`${site.id}-av`} style={{ color: 'var(--ink)' }}>
                    Available
                  </th>,
                ])}
            </tr>
          </thead>
          <tbody>
            {groups.flatMap((group) =>
              group.rows.flatMap((row, i) => {
                const isOpen = expanded.has(row.sku.id);
                const isLast = i === group.rows.length - 1;
                const rowClass = [isOpen ? s.open : '', isLast ? s.end : ''].filter(Boolean).join(' ') || undefined;
                const mainRow = (
                  <tr key={row.sku.id} className={rowClass} data-testid={`row-${row.sku.id}`} onClick={() => toggleRow(row.sku.id)} style={{ cursor: 'pointer' }}>
                    {i === 0 && (
                      <td className={s.grp} rowSpan={group.rows.length + (expandSite ? group.rows.filter((r) => expanded.has(r.sku.id)).length : 0)}>
                        <b>{group.type}</b>
                        <small>
                          {group.jacket ? (
                            <>
                              {group.jacket}
                              <br />
                            </>
                          ) : null}
                          {group.rows.length} SKU{group.rows.length === 1 ? '' : 's'}
                        </small>
                      </td>
                    )}
                    <td className={s.l}>
                      <SkuTag sku={row.sku} withCode />
                      {row.flags.map((f) => (
                        <span key={f.level} className={`${s.flag} ${s[f.level]}`}>
                          {FLAG_WORD[f.level]} at {f.sites.map((x) => x.shortCode).join(', ')}
                        </span>
                      ))}
                    </td>
                    {displaySites.flatMap((site) => {
                      if (!site.live) return [<td key={site.id} className={`${s.kc} ${s.gs}`}>—</td>];
                      const stockRow = row.perSite.get(site.id);
                      const inStock = stockRow?.inStock ?? 0;
                      const reserved = stockRow?.reserved ?? 0;
                      const available = stockRow?.available ?? 0;
                      return [
                        <td key={`${site.id}-in`} className={s.gs}>{inStock}</td>,
                        <td key={`${site.id}-res`}>{reserved}</td>,
                        <td key={`${site.id}-av`} className={`${s.av}${available < 0 ? ' neg' : ''}`}>
                          {available}
                        </td>,
                      ];
                    })}
                    <td className={`${s.tot} ${s.gs}`} data-testid="total">
                      {row.totalAvailable} <span>· {row.totalInStock}</span>
                    </td>
                    <td className={s.chev}>
                      <Icon name={isOpen ? 'chev-down' : 'chev-right'} />
                    </td>
                  </tr>
                );
                return isOpen && expandSite
                  ? [mainRow, <StockExpansion key={`${row.sku.id}-x`} site={expandSite} sku={row.sku} totalCols={totalCols - 1} />]
                  : [mainRow];
              }),
            )}
            {groups.length === 0 && (
              <tr>
                <td colSpan={totalCols} className="muted" style={{ padding: 20, textAlign: 'center' }}>
                  No SKUs match.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
